import React from 'react';
import { Link } from 'react-router-dom';
import { Product, FALLBACK_PRODUCT_IMAGE } from '../data/products';
import { ShoppingBag, Star } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
interface ProductCardProps {
  product: Product;
}
export function ProductCard({
  product
}: ProductCardProps) {
  const {
    dispatch
  } = useCart();
  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigating to product detail
    // If product has required options (sizes or colors), redirect to product page instead
    if (product.sizes?.length || product.colors?.length) {
      toast('Please select options first', {
        description: 'This product requires size or color selection.'
      });
      return;
    }
    const price = product.salePrice || product.price;
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: product.id,
        productId: product.id,
        product,
        quantity: 1,
        selectedAddOns: [],
        unitPrice: price
      }
    });
    toast.success('Added to cart', {
      description: `${product.name} has been added to your cart.`
    });
  };
  return <Link to={`/product/${product.slug}`} className="group flex flex-col bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-md transition-all duration-300" data-id="element-207">
      {/* Image Container */}
      <div className="relative aspect-[4/5] overflow-hidden bg-gray-100" data-id="element-208">
        <img src={product.images[0]} alt={product.name} onError={e => {
        const el = e.currentTarget;
        if (el.src !== FALLBACK_PRODUCT_IMAGE) el.src = FALLBACK_PRODUCT_IMAGE;
      }} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" data-id="element-209" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2" data-id="element-210">
          {product.salePrice && <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-211">
              Sale
            </span>}
        </div>

        {/* Quick Add Button (Desktop Hover) */}
        <div className="absolute bottom-4 left-0 right-0 px-4 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hidden md:block" data-id="element-212">
          <button onClick={handleQuickAdd} className="w-full bg-white/90 backdrop-blur-sm text-gray-900 font-medium py-2.5 rounded-lg shadow-sm hover:bg-white flex items-center justify-center gap-2 text-sm" data-id="element-213">
            <ShoppingBag className="w-4 h-4" data-id="element-214" /> Quick Add
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-grow" data-id="element-215">
        <div className="text-xs text-gray-500 mb-1 capitalize" data-id="element-216">
          {product.category}
        </div>
        <h3 className="text-sm font-medium text-gray-900 line-clamp-1 mb-1 group-hover:text-red-600 transition-colors" data-id="element-217">
          {product.name}
        </h3>

        <div className="flex items-center gap-1 mb-2" data-id="element-218">
          <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" data-id="element-219" />
          <span className="text-xs font-medium text-gray-700" data-id="element-220">
            {product.rating}
          </span>
          <span className="text-xs text-gray-400" data-id="element-221">
            ({product.reviewsCount})
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between" data-id="element-222">
          <div className="flex items-center gap-2" data-id="element-223">
            <span className="font-semibold text-gray-900" data-id="element-224">
              MVR{' '}
              {product.salePrice ? product.salePrice.toFixed(2) : product.price.toFixed(2)}
            </span>
            {product.salePrice && <span className="text-xs text-gray-400 line-through" data-id="element-225">
                MVR {product.price.toFixed(2)}
              </span>}
          </div>

          {/* Mobile Quick Add */}
          <button onClick={handleQuickAdd} className="md:hidden w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-red-50 hover:text-red-600" data-id="element-226">
            <ShoppingBag className="w-4 h-4" data-id="element-227" />
          </button>
        </div>
      </div>
    </Link>;
}